import React, { useState, useEffect } from 'react';
import { Scale, Info, RotateCcw, AlertTriangle } from 'lucide-react';
// Custom CSS for animations and toggles
const customStyles = `
  .gauge-needle {
    transition: transform 1s cubic-bezier(0.65, 0, 0.35, 1);
  }
  .toggle-checkbox:checked {
    right: 0;
    border-color: #10B981;
  }
  .toggle-checkbox:checked + .toggle-label {
    background-color: #10B981;
  }
`;

// Data Structure based on the Document
const checklistData = [
  {
    id: 1,
    title: "1. Integridad y autenticidad de CFDI",
    desc: "CFF: sanciones de 2 a 9 años, prisión preventiva, fiscalización sobre operaciones inexistentes.",
    maxScore: 5,
    questions: [
      { id: "1-1", text: "¿Tu empresa opera con motores de validación masiva de CFDI y trazabilidad con ERP?", valueIfNo: 1 },
      { id: "1-2", text: "¿Existe monitoreo automatizado para detectar CFDI no soportados por evidencia operativa?", valueIfNo: 2 },
      { id: "1-3", text: "¿Cuentas con listas internas de proveedores autorizados y filtros de EFOS/EDOS?", valueIfNo: 2 }
    ]
  },
  {
    id: 2,
    title: "2. Cumplimiento normativo y gobierno corporativo",
    desc: "CFF: facultades ampliadas del SAT para suspender actividades / obligaciones.",
    maxScore: 4,
    questions: [
      { id: "2-1", text: "¿La gobernanza fiscal asegura RFC actualizado, obligaciones vigentes y auditorías internas periódicas?", valueIfNo: 1 },
      { id: "2-2", text: "¿Existe una función interna de compliance que identifique omisiones de declaraciones antes de que escalen?", valueIfNo: 2 },
      { id: "2-3", text: "¿Operan tableros ejecutivos de cumplimiento para mitigar riesgos de suspensión de actividades?", valueIfNo: 1 }
    ]
  },
  {
    id: 3,
    title: "3. Gestión financiera y costo de cumplimiento",
    desc: "CFF: incremento de recargos del 1.26% al 1.38% mensual.",
    maxScore: 3,
    questions: [
      { id: "3-1", text: "¿La tesorería mantiene cero pagos extemporáneos y control predictivo de obligaciones?", valueIfNo: 2 },
      { id: "3-2", text: "¿Modelas el impacto financiero del incremento de recargos en pasivos fiscales?", valueIfNo: 1 }
    ]
  },
  {
    id: 4,
    title: "4. Defensa fiscal: EFOS / EDOS",
    desc: "RMF: nuevas reglas 2.9.15–2.9.18 para aclaraciones y desvirtuación.",
    maxScore: 4,
    questions: [
      { id: "4-1", text: "¿Tu corporativo cuenta con protocolos formales de desvirtuación con evidencia documental robusta?", valueIfNo: 2 },
      { id: "4-2", text: "¿Existe un expediente digital centralizado por proveedor/operación para aclaraciones en tiempo real?", valueIfNo: 1 },
      { id: "4-3", text: "¿Hay monitoreo continuo de listas negras del SAT y sistemas expertos que alerten sobre los riesgos?", valueIfNo: 1 }
    ]
  },
  {
    id: 5,
    title: "5. Actualización Regulatoria y Documental",
    desc: "RMF: reorganización de anexos, actualización de trámites y formas oficiales.",
    maxScore: 3,
    questions: [
      { id: "5-1", text: "¿El área fiscal mapea las nuevas fichas de trámites del Anexo 2 y las integra a procesos internos?", valueIfNo: 1 },
      { id: "5-2", text: "¿Están actualizadas las formas oficiales del Anexo 1 e incorporadas en los flujos de cumplimiento?", valueIfNo: 1 },
      { id: "5-3", text: "¿Tu corporativo monitorea y ejecuta las facilidades administrativas y prórrogas vigentes para 2026?", valueIfNo: 1 }
    ]
  }
];

const riskLevels = [
  { min: 0, max: 3, label: "Riesgo Mínimo (Estándar AAA)", score: "1/10", color: "bg-green-500", text: "text-green-700", border: "border-green-500", desc: "Cumplimiento óptimo. Mantener vigilancia." },
  { min: 4, max: 6, label: "Riesgo Bajo", score: "3/10", color: "bg-lime-400", text: "text-lime-700", border: "border-lime-400", desc: "Buenas prácticas, pero con áreas de oportunidad menores." },
  { min: 7, max: 10, label: "Riesgo Moderado", score: "5/10", color: "bg-yellow-400", text: "text-yellow-700", border: "border-yellow-400", desc: "Exposición media. Se recomienda revisión de procesos clave." },
  { min: 11, max: 14, label: "Riesgo Elevado", score: "7/10", color: "bg-orange-500", text: "text-orange-700", border: "border-orange-500", desc: "Peligro considerable de sanciones o auditorías. Acción correctiva necesaria." },
  { min: 15, max: 19, label: "Riesgo Crítico", score: "10/10", color: "bg-red-600", text: "text-red-800", border: "border-red-600", desc: "Alto riesgo operativo, financiero y reputacional. ¡Atención Inmediata!" }
];

// Toggle Component
const Toggle = ({ checked, onChange }) => (
  <div className="relative inline-block w-12 mr-2 align-middle select-none transition duration-200 ease-in">
    <input 
      type="checkbox" 
      name="toggle" 
      id="toggle" 
      checked={checked} 
      onChange={onChange}
      className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer transition-all duration-300 ease-in-out"
      style={{ 
        right: checked ? '0' : 'auto', 
        left: checked ? 'auto' : '0',
        borderColor: checked ? '#10B981' : '#EF4444'
      }}
    />
    <label 
      className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer ${checked ? 'bg-green-400' : 'bg-red-400'}`}
    ></label>
  </div>
);

// Question Row Component
const QuestionRow = ({ q, value, onChange }) => (
  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 bg-white border border-gray-100 rounded-lg mb-3 shadow-sm hover:shadow-md transition-shadow">
    <div className="w-full sm:w-3/4 pr-4 mb-3 sm:mb-0">
      <p className="text-gray-800 font-medium">{q.text}</p>
      <p className="text-xs text-gray-400 mt-1">
        Riesgo si NO: <span className="font-bold text-red-500">+{q.valueIfNo} pts</span>
      </p>
    </div>
    <div className="flex items-center w-full sm:w-1/4 justify-between sm:justify-end">
      <span className={`mr-3 text-sm font-bold ${value === 'si' ? 'text-green-600' : 'text-gray-400'}`}>SÍ</span>
      <Toggle 
        checked={value === 'si'} 
        onChange={() => onChange(q.id, value === 'si' ? 'no' : 'si')} 
      />
      <span className={`ml-3 text-sm font-bold ${value === 'no' ? 'text-red-600' : 'text-gray-400'}`}>NO</span>
    </div>
  </div>
);

// Semaphore Chart Component
const SemaphorChart = ({ score }) => {
  const percentage = Math.min((score / 19) * 100, 100);
  
  // Determine active level
  const currentLevel = riskLevels.find(l => score >= l.min && score <= l.max) || riskLevels[4];

  return (
    
    <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200 mb-8 sticky top-4 z-10">
      <h3 className="text-xl font-bold text-gray-800 mb-4 text-center">Nivel de Riesgo Fiscal 2026</h3>
      
      {/* Gauge Bar */}
      <div className="relative h-8 w-full bg-gray-200 rounded-full overflow-hidden mb-2">
        <div className="absolute top-0 left-0 h-full w-full flex">
          <div className="h-full bg-green-500 w-[21%]"></div>   {/* 0-4 approx */}
          <div className="h-full bg-lime-400 w-[15%]"></div>    {/* 4-7 approx */}
          <div className="h-full bg-yellow-400 w-[21%]"></div>  {/* 7-11 approx */}
          <div className="h-full bg-orange-500 w-[21%]"></div>  {/* 11-15 approx */}
          <div className="h-full bg-red-600 w-[22%]"></div>     {/* 15-19 approx */}
        </div>
        
        {/* Needle */}
        <div 
          className="gauge-needle absolute top-0 h-full w-2 bg-black border-2 border-white shadow-lg transform -translate-x-1/2"
          style={{ left: `${percentage}%` }}
        ></div>
      </div>

      <div className="flex justify-between text-xs text-gray-500 mb-6 font-mono">
        <span>0 pts (Óptimo)</span>
        <span>19 pts (Crítico)</span>
      </div>

      {/* Result Card */}
      <div className={`p-4 rounded-lg border-l-0 ${currentLevel.color.replace('bg-', 'bg-opacity-10 ')} ${currentLevel.border} flex flex-col items-center justify-center text-center`}>
        <div className="text-4xl font-black mb-1 text-gray-800">
          {score} <span className="text-lg font-normal text-gray-500">/ 19 pts</span>
        </div>
        <h4 className={`text-2xl font-bold ${currentLevel.text} mb-2`}>{currentLevel.label}</h4>
        <div className="px-3 py-1 bg-gray-800 text-white text-xs font-bold rounded-full mb-3">
          Escala: {currentLevel.score}
        </div>
        <p className="text-sm text-gray-700">{currentLevel.desc}</p>
      </div>
    </div>
  );
};

export default function App() {
  // Initial State: All 'si' (0 points)
  const [answers, setAnswers] = useState(() => {
    const initialState = {};
    checklistData.forEach(section => {
      section.questions.forEach(q => {
        initialState[q.id] = 'si';
      });
    });
    return initialState;
  });

  const [totalScore, setTotalScore] = useState(0);

  // Calculate Score
  useEffect(() => {
    let score = 0;
    checklistData.forEach(section => {
      section.questions.forEach(q => {
        if (answers[q.id] === 'no') {
          score += q.valueIfNo;
        }
      });
    });
    setTotalScore(score);
  }, [answers]);

  const handleAnswerChange = (id, value) => {
    setAnswers(prev => ({ ...prev, [id]: value }));
  };

  const resetForm = () => {
    const resetState = {};
    checklistData.forEach(section => {
      section.questions.forEach(q => {
        resetState[q.id] = 'si';
      });
    });
    setAnswers(resetState);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    
    <div className="min-h-screen bg-slate-100 font-sans text-slate-900">
      <style>{customStyles}</style>
      <div className="max-w-8xl mx-auto p-4 md:p-8">
        {/* Header */}


        <div className="flex flex-col-reverse lg:flex-row gap-8">
          
          {/* Questions Column */}
          <div className="lg:w-2/3">
            {checklistData.map((section) => (
              <div key={section.id} className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 overflow-hidden">
                <div className="bg-slate-50 p-4 border-b border-gray-200">
                  <h2 className="text-lg font-bold text-slate-800 flex items-center">
                    <span className="bg-slate-800 text-white w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm">
                      {section.id}
                    </span>
                    {section.title}
                  </h2>
                  <p className="text-xs text-gray-500 mt-1 ml-11">{section.desc}</p>
                </div>
                <div className="p-4">
                  {section.questions.map(q => (
                    <QuestionRow 
                      key={q.id} 
                      q={q} 
                      value={answers[q.id]} 
                      onChange={handleAnswerChange} 
                    />
                  ))}
                </div>
                <div className="bg-gray-50 px-4 py-2 border-t border-gray-100 flex justify-end">
                  <span className="text-xs font-semibold text-gray-400">Máximo: {section.maxScore} pts</span>
                </div>
              </div>
            ))}
            
            <button 
              onClick={resetForm}
              className="w-full py-4 bg-indigo-950 text-white font-bold rounded-xl hover:bg-violet-400 transition flex items-center justify-center"
            >
              <RotateCcw size={20} className="mr-2" /> Reiniciar Evaluación
            </button>
          </div>

          {/* Result Column (Sticky) */}
          <div className="lg:w-1/3">
            <SemaphorChart score={totalScore} />
            
            <div className="bg-violet-100 border border-violet-200 p-5 rounded-xl text-sm text-blue-800">
              <h4 className="font-bold mb-2 flex items-center">
                <AlertTriangle size={18} className="mr-2" />
                ¿Riesgo Elevado o Crítico?
              </h4>
              <p className="mb-3">
                Si tu corporativo requiere una evaluación puntual, se recomienda un análisis de riesgo documental y CFDI basado en evidencia.
              </p>
            </div>
          </div>
          
        </div>
        
        <footer className="mt-12 text-center text-gray-400 text-xs pb-8">
          <p>© 2026 Herramienta de Autoevaluación Fiscal.</p>
        </footer>
      </div>
    </div>
  );
}